import * as React from 'react';
import { Input } from '../Input/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../Select/Select';
import { Switch } from '../Switch/Switch';
import { Button } from '../Button/Button';
import { Icons } from '../Icons';
import { cn } from '../../lib/utils';

/**
 * Light-surface tokens for the download-image modal. The viewer root uses dark
 * shadcn tokens; portaled modal content needs readable labels, inputs, and CTAs.
 */
export const IMAGE_MODAL_LIGHT_SURFACE = cn(
  '[--background:0_0%_100%]',
  '[--foreground:215_25%_17%]',
  '[--card:0_0%_100%]',
  '[--card-foreground:215_25%_17%]',
  '[--popover:0_0%_100%]',
  '[--popover-foreground:215_25%_17%]',
  '[--muted:210_22%_95%]',
  '[--muted-foreground:215_14%_46%]',
  '[--border:214_20%_88%]',
  '[--input:214_20%_88%]',
  '[--accent:210_25%_95%]',
  '[--accent-foreground:215_25%_17%]',
  '[--secondary-foreground:215_25%_17%]',
  '[--primary:192_84%_38%]',
  '[--primary-foreground:0_0%_100%]',
  '[--ring:192_84%_38%]'
);

/** Right panel typography — Figma: Inter 14px / 400 / 20px line-height */
const IMAGE_MODAL_RIGHT_TEXT = 'font-inter text-[14px] font-normal leading-[20px] tracking-[0]';
const IMAGE_MODAL_FIELD_LABEL = cn(IMAGE_MODAL_RIGHT_TEXT, 'block text-[#0f172a]');
const IMAGE_MODAL_FIELD_INPUT = cn(
  IMAGE_MODAL_RIGHT_TEXT,
  'h-9 w-full rounded-[5px] border-[#c7cbcf] px-5 text-[#0d1218] shadow-none placeholder:text-[#737373]'
);

interface ImageModalProps {
  children: React.ReactNode;
  className?: string;
}

export function ImageModal({ children, className }: ImageModalProps) {
  return (
    <div
      className={cn(
        IMAGE_MODAL_LIGHT_SURFACE,
        'flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white text-[#0f172a]',
        className
      )}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* HeaderBar subcomponent */

interface HeaderBarProps {
  title: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  className?: string;
}

function HeaderBar({ title, onClose, closeLabel = 'Close', className }: HeaderBarProps) {
  return (
    <div
      className={cn(
        'flex h-[62px] shrink-0 items-center justify-between border-b border-[#cbd5e1] bg-white px-6',
        className
      )}
    >
      <h2 className="font-inter text-[18px] font-semibold leading-[18px] tracking-[0] text-[#0f172a]">
        {title}
      </h2>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0 opacity-70 hover:bg-[#f5f5f5] hover:opacity-100"
        onClick={onClose}
        aria-label={closeLabel}
        data-cy="close-button"
      >
        <Icons.Close className="h-9 w-9 text-[#0f172a]" />
      </Button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Body subcomponent */

interface ImageBodyProps {
  children: React.ReactNode;
  className?: string;
}

function ImageBody({ children, className }: ImageBodyProps) {
  return (
    <div
      className={cn(
        'download-image-modal-body grid min-h-0 flex-1 grid-cols-[500px_500px] overflow-hidden',
        className
      )}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* ImageVisual subcomponent */

interface ImageVisualProps {
  className?: string;
  children: React.ReactNode;
}

function ImageVisual({ children, className }: ImageVisualProps) {
  return (
    <div
      className={cn(
        'download-image-modal-preview relative h-full min-h-[560px] min-w-0 overflow-hidden bg-[#0d0d0d]',
        className
      )}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* ImageOptions subcomponent */

interface ImageOptionsProps {
  children: React.ReactNode;
  className?: string;
}

function ImageOptions({ children, className }: ImageOptionsProps) {
  return (
    <div
      className={cn(
        IMAGE_MODAL_RIGHT_TEXT,
        'flex min-h-0 min-w-0 flex-col gap-5 overflow-y-auto border-l border-[#e5e5e5] px-6 py-6 text-[#11171d]',
        className
      )}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Divider subcomponent */

interface DividerProps {
  className?: string;
}

function Divider({ className }: DividerProps) {
  return <div className={cn('h-px w-full shrink-0 border-t border-[#dfe1e4]', className)} />;
}

/* -------------------------------------------------------------------------- */
/* Filename subcomponent */

interface FilenameProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children'> {
  children: React.ReactNode;
  className?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

function Filename({ children, className, value, onChange, ...props }: FilenameProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label className={IMAGE_MODAL_FIELD_LABEL}>{children}</label>
      <Input
        {...props}
        className={cn(IMAGE_MODAL_FIELD_INPUT, className)}
        value={value}
        onChange={onChange ?? (() => {})}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Filetype subcomponent */

interface FiletypeProps {
  children: React.ReactNode;
  selected: string;
  onSelect?: (val: string) => void;
  className?: string;
  options?: Array<{ value: string; label: string }>;
}

function Filetype({
  children,
  selected,
  onSelect,
  className,
  options = [],
}: FiletypeProps) {
  const defaultOptions = [
    { value: 'jpg', label: 'JPG — JPEG Image' },
    { value: 'png', label: 'PNG — Portable Network Graphics' },
  ];

  const fileTypeOptions = options.length ? options : defaultOptions;

  return (
    <div className={cn('space-y-1.5', className)}>
      <label className={IMAGE_MODAL_FIELD_LABEL}>{children}</label>
      <Select
        value={selected}
        onValueChange={val => onSelect?.(val)}
      >
        <SelectTrigger
          aria-label="File type"
          className={IMAGE_MODAL_FIELD_INPUT}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className={IMAGE_MODAL_LIGHT_SURFACE}>
          {fileTypeOptions.map(option => (
            <SelectItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* ImageSize subcomponent */

interface ImageSizeProps {
  width: string;
  height: string;
  onWidthChange?: React.ChangeEventHandler<HTMLInputElement>;
  onHeightChange?: React.ChangeEventHandler<HTMLInputElement>;
  className?: string;
  maxWidth?: string;
  maxHeight?: string;
}

function ImageSize({
  width,
  height,
  onWidthChange,
  onHeightChange,
  className,
  maxWidth,
  maxHeight,
}: ImageSizeProps) {
  return (
    <div className={cn('flex gap-2.5', className)}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <label className={IMAGE_MODAL_FIELD_LABEL}>Width (px)</label>
        <Input
          value={width}
          onChange={onWidthChange ?? (() => {})}
          placeholder="Width"
          className={IMAGE_MODAL_FIELD_INPUT}
          max={maxWidth}
        />
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <label className={IMAGE_MODAL_FIELD_LABEL}>Height (px)</label>
        <Input
          value={height}
          onChange={onHeightChange ?? (() => {})}
          placeholder="Height"
          className={IMAGE_MODAL_FIELD_INPUT}
          max={maxHeight}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* SwitchOption subcomponent */

interface SwitchOptionProps {
  children: React.ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
}

function SwitchOption({
  children,
  checked,
  defaultChecked,
  onCheckedChange,
  className,
}: SwitchOptionProps) {
  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <span className={cn(IMAGE_MODAL_RIGHT_TEXT, 'text-[#11171d]')}>{children}</span>
      <Switch
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={val => onCheckedChange?.(val)}
        className="h-[18px] w-8 shrink-0 data-[state=checked]:bg-[#0f91b2] data-[state=unchecked]:bg-[#cbd5e1]"
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Attach subcomponents onto the main ImageModal function. */

ImageModal.HeaderBar = HeaderBar;
ImageModal.Body = ImageBody;
ImageModal.ImageVisual = ImageVisual;
ImageModal.ImageOptions = ImageOptions;
ImageModal.Divider = Divider;
ImageModal.Filename = Filename;
ImageModal.Filetype = Filetype;
ImageModal.ImageSize = ImageSize;
ImageModal.SwitchOption = SwitchOption;
